import React from "react";
import Card from "../../../../components/Card/Card";
import CardBody from "../../../../components/Card/CardBody";
import { ProductSelectionTable } from "./Products/ProductSelectionTable";
import TranslationWrapper, { getTranslation } from "../../../../components/TranslationWrapper/TranslationWrapper";
import AsynchronousAutocomplete from "../../../../components/Autocomplete/AsynchronousAutocomplete";
import { VestigingFormContext } from "../../../Vestigingen/VestigingForm";
import { useFieldArray, useFormContext } from "react-hook-form";
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Delete, Visibility } from "@mui/icons-material";
import { Link } from "react-router";
import Tooltip from "@mui/material/Tooltip";
import Button from "../../../../components/CustomButtons/Button";

type MenuResponse = {
  menus: {
    naam: string;
    value: string;
    label: string;
  }[];
};

export function MenuSelectionTable() {
  const { control, menukaartVestigingId }: VestigingFormContext = useFormContext<MenuResponse & VestigingFormContext>();
  const { fields, replace, remove } = useFieldArray<MenuResponse>({
    control,
    name: "menus",
    keyName: "value",
  });

  return (
    <>
      <AsynchronousAutocomplete
        async={{
          url: `core/kaarten/search?fields=id as value,id,naam as label,vestiging_id,naam&per_page=1000&sort_field=naam&sort_dir=asc&vestiging_id=${menukaartVestigingId}`,
          searchField: "naam",
        }}
        key={menukaartVestigingId}
        multiple={true}
        onChange={(e, option) => {
          replace(option);
        }}
        placeholder={getTranslation("Find menu")}
        value={(fields ?? []).map((field) => {
          field.value = field.id = field.id ?? field.value;
          field.label = field.naam = field.naam ?? field.label;

          return field;
        })}
      />
      {fields.length > 0 ? (
        <TableContainer sx={{ mt: 2 }}>
          <Table size={"small"}>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>
                  <TranslationWrapper tDefault={"Name"} tKey={"Name"} />
                </TableCell>
                <TableCell>
                  <TranslationWrapper tDefault={"Actions"} tKey={"Actions"} />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                "& tr:nth-child(odd)": {
                  backgroundColor: "#eee",
                },
              }}
            >
              {fields.map((field, index) => {
                return (
                  <TableRow key={field.id}>
                    <TableCell>
                      <Typography sx={{ color: "#555" }}>#{field.id}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>{field.naam}</Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          remove(index);
                        }}
                      >
                        <Delete />
                      </IconButton>

                      <Link to={`/manager/kaarten/${field.id}/edit`}>
                        <Tooltip
                          placement={"bottom"}
                          title={<TranslationWrapper tDefault={"View Menu"} tKey={"global.jamezzTable.viewMenu"} />}
                        >
                          <Button color={"success"} simple>
                            <Visibility />
                          </Button>
                        </Tooltip>
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </>
  );
}

export default function Products() {
  return (
    <>
      <Card>
        <CardBody>
          <h4>
            <TranslationWrapper
              tDefault={"Products available in this arrangement group"}
              tKey={"ayce.forms.arrangementGroups.products.heading"}
            />
          </h4>
          <p>
            <TranslationWrapper
              tDefault="All products and menus configured here will be available in all linked arrangements. Menu and product rules
            such as time schedules, disabled, blocked and others will still apply."
              tKey={"ayce.forms.arrangementGroups.products.explanation"}
            />
          </p>
        </CardBody>
      </Card>
      <Card>
        <CardBody>
          <h4>
            <TranslationWrapper tDefault={"Menus"} tKey={"Menus"} />
          </h4>
          <MenuSelectionTable />
        </CardBody>
      </Card>
      <Card>
        <CardBody>
          <h4>
            <TranslationWrapper tDefault={"Products"} tKey={"Products"} />
          </h4>
          <ProductSelectionTable />
        </CardBody>
      </Card>
    </>
  );
}
